import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Types
export interface User {
  id: string;
  email: string;
  display_name: string;
  avatar_url: string | null;
  language: 'ar' | 'en';
  theme: 'light' | 'dark';
}

export interface Room {
  id: string;
  user_id: string;
  title_ar: string | null;
  title_en: string | null;
  description_ar: string | null;
  description_en: string | null;
  is_public: boolean;
  created_at: string;
  updated_at: string;
}

export interface Task {
  id: string;
  room_id: string;
  document_id: string | null;
  title_ar: string | null;
  title_en: string | null;
  description_ar: string | null;
  description_en: string | null;
  status: 'pending' | 'in-progress' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  assigned_to: string | null;
  created_by: string;
  due_date: string | null;
  position: number;
  created_at: string;
  updated_at: string;
}

export interface UserPresence {
  userId: string;
  username: string;
  color: string;
  cursor: { line: number; ch: number } | null;
  selection: { from: number; to: number } | null;
  lastSeen: number;
}

// Auth Store
interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  
  setUser: (user: User | null) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: true,
      
      setUser: (user) => set({ 
        user, 
        isAuthenticated: !!user,
        isLoading: false,
      }),
      
      setTokens: (accessToken, refreshToken) => set({ 
        accessToken, 
        refreshToken,
        isAuthenticated: true,
      }),
      
      logout: () => set({ 
        user: null, 
        accessToken: null, 
        refreshToken: null, 
        isAuthenticated: false,
      }),
      
      setLoading: (isLoading) => set({ isLoading }),
    }),
    {
      name: 'razmucode-auth',
      partialize: (state) => ({ 
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// App Settings Store
interface SettingsState {
  language: 'ar' | 'en';
  theme: 'light' | 'dark';
  sidebarOpen: boolean;
  
  setLanguage: (language: 'ar' | 'en') => void;
  setTheme: (theme: 'light' | 'dark') => void;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      language: 'ar',
      theme: 'light',
      sidebarOpen: true,
      
      setLanguage: (language) => {
        // Update document direction
        document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
        document.documentElement.lang = language;
        set({ language });
      },
      
      setTheme: (theme) => {
        // Update document class
        document.documentElement.classList.toggle('dark', theme === 'dark');
        document.documentElement.setAttribute('data-theme', theme);
        set({ theme });
      },
      
      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
      setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),
    }),
    {
      name: 'razmucode-settings',
    }
  )
);

// Room Store
interface RoomState {
  rooms: Room[];
  currentRoom: Room | null;
  currentRoomMembers: UserPresence[];
  isLoading: boolean;
  
  setRooms: (rooms: Room[]) => void;
  addRoom: (room: Room) => void;
  updateRoom: (roomId: string, updates: Partial<Room>) => void;
  removeRoom: (roomId: string) => void;
  setCurrentRoom: (room: Room | null) => void;
  setCurrentRoomMembers: (members: UserPresence[]) => void;
  updateRoomMember: (member: UserPresence) => void;
  removeRoomMember: (userId: string) => void;
  setLoading: (loading: boolean) => void;
}

export const useRoomStore = create<RoomState>()((set) => ({
  rooms: [],
  currentRoom: null,
  currentRoomMembers: [],
  isLoading: false,
  
  setRooms: (rooms) => set({ rooms }),
  
  addRoom: (room) => set((state) => ({ 
    rooms: [room, ...state.rooms] 
  })),
  
  updateRoom: (roomId, updates) => set((state) => ({
    rooms: state.rooms.map(r => 
      r.id === roomId ? { ...r, ...updates } : r
    ),
    currentRoom: state.currentRoom?.id === roomId 
      ? { ...state.currentRoom, ...updates }
      : state.currentRoom,
  })),
  
  removeRoom: (roomId) => set((state) => ({
    rooms: state.rooms.filter(r => r.id !== roomId),
    currentRoom: state.currentRoom?.id === roomId ? null : state.currentRoom,
  })),
  
  setCurrentRoom: (currentRoom) => set({ currentRoom }),
  
  setCurrentRoomMembers: (currentRoomMembers) => set({ currentRoomMembers }),
  
  updateRoomMember: (member) => set((state) => {
    const existing = state.currentRoomMembers.find(m => m.userId === member.userId);
    if (existing) {
      return {
        currentRoomMembers: state.currentRoomMembers.map(m =>
          m.userId === member.userId ? member : m
        ),
      };
    }
    return {
      currentRoomMembers: [...state.currentRoomMembers, member],
    };
  }),
  
  removeRoomMember: (userId) => set((state) => ({
    currentRoomMembers: state.currentRoomMembers.filter(m => m.userId !== userId),
  })),
  
  setLoading: (isLoading) => set({ isLoading }),
}));

// Task Store
interface TaskState {
  tasks: Task[];
  selectedTask: Task | null;
  filter: {
    status: string | null;
    priority: string | null;
    assignedTo: string | null;
  };
  viewMode: 'list' | 'kanban';
  isLoading: boolean;
  
  setTasks: (tasks: Task[]) => void;
  addTask: (task: Task) => void;
  updateTask: (taskId: string, updates: Partial<Task>) => void;
  removeTask: (taskId: string) => void;
  setSelectedTask: (task: Task | null) => void;
  setFilter: (filter: Partial<TaskState['filter']>) => void;
  clearFilters: () => void;
  setViewMode: (mode: 'list' | 'kanban') => void;
  setLoading: (loading: boolean) => void;
  reorderTasks: (tasks: Task[]) => void;
}

export const useTaskStore = create<TaskState>()((set) => ({
  tasks: [],
  selectedTask: null,
  filter: {
    status: null,
    priority: null,
    assignedTo: null,
  },
  viewMode: 'list',
  isLoading: false,
  
  setTasks: (tasks) => set({ tasks }),
  
  addTask: (task) => set((state) => ({ 
    tasks: [...state.tasks, task] 
  })),
  
  updateTask: (taskId, updates) => set((state) => ({
    tasks: state.tasks.map(t => 
      t.id === taskId ? { ...t, ...updates } : t
    ),
    selectedTask: state.selectedTask?.id === taskId 
      ? { ...state.selectedTask, ...updates }
      : state.selectedTask,
  })),
  
  removeTask: (taskId) => set((state) => ({
    tasks: state.tasks.filter(t => t.id !== taskId),
    selectedTask: state.selectedTask?.id === taskId ? null : state.selectedTask,
  })),
  
  setSelectedTask: (selectedTask) => set({ selectedTask }),
  
  setFilter: (filter) => set((state) => ({
    filter: { ...state.filter, ...filter },
  })),
  
  clearFilters: () => set({
    filter: { status: null, priority: null, assignedTo: null },
  }),
  
  setViewMode: (viewMode) => set({ viewMode }),
  
  setLoading: (isLoading) => set({ isLoading }),
  
  reorderTasks: (tasks) => set({ tasks }),
}));

// Notification Store
export interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
}

interface NotificationState {
  notifications: Notification[];
  
  addNotification: (notification: Omit<Notification, 'id'>) => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
}

export const useNotificationStore = create<NotificationState>()((set) => ({
  notifications: [],
  
  addNotification: (notification) => {
    const id = Math.random().toString(36).substring(2, 9);
    set((state) => ({
      notifications: [...state.notifications, { ...notification, id }],
    }));
    
    // Auto-remove after duration
    const duration = notification.duration ?? 5000;
    if (duration > 0) {
      setTimeout(() => {
        set((state) => ({
          notifications: state.notifications.filter(n => n.id !== id),
        }));
      }, duration);
    }
  },
  
  removeNotification: (id) => set((state) => ({
    notifications: state.notifications.filter(n => n.id !== id),
  })),
  
  clearAll: () => set({ notifications: [] }),
}));
